/**
 * The decisions `MultiSelect` makes, separated from the DOM that renders them.
 *
 * Two reasons, and the second is the real one. There is no jsdom or
 * testing-library in this workspace — `vitest.config.ts` runs plain Node, and
 * every web spec here is a pure function (`stone-selector.spec.ts`,
 * `order-lines.spec.ts`) with the interaction proved in Playwright. Adding a DOM
 * test stack to unit-test one component is a dependency decision for the whole
 * team, not a side effect of a control.
 *
 * The first reason is that these three functions are where a multi-select is
 * actually wrong or right — what the trigger says, what the search shows, and
 * what order a selection serialises in — and none of them need a browser.
 */

export interface MultiSelectOption {
  /** What goes into `value` — an attribute-value id, or a code for carat. */
  v: string;
  label: string;
}

/**
 * CR-46 — "the trigger shows the chosen labels and switches to a count past
 * four". Four is the prototype's own number (`admin-console.html`'s
 * `ddSummary`), and it is a readability limit rather than a round one: five
 * karats spelled out overflow the trigger and tell a reader less than "5 of 9
 * selected" does.
 *
 * `null` for an empty selection, so the caller shows its placeholder rather
 * than an empty string that looks like a rendering fault.
 */
export function summarise(
  options: readonly MultiSelectOption[],
  value: readonly string[],
  limit = 4,
): string | null {
  const chosen = new Set(value);
  const labels = options.filter((o) => chosen.has(o.v)).map((o) => o.label);
  if (labels.length === 0) return null;
  return labels.length > limit
    ? `${labels.length} of ${options.length} selected`
    : labels.join(', ');
}

/** Case-insensitive, matches anywhere in the label — a karat is found by "18"
 *  as readily as by "18k", and a clarity by "vs". */
export function filterOptions(
  options: readonly MultiSelectOption[],
  query: string,
): MultiSelectOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...options];
  return options.filter((o) => o.label.toLowerCase().includes(needle));
}

/**
 * Add or remove one value, returning the selection **in option order**.
 *
 * Click order would mean two identical selections serialise differently
 * depending on the sequence they were made in, so a save would look like a
 * change when nothing had changed, and a purity list would read 22k·10k·18k on
 * screen. Option order is the master list's order, which is the grading ladder
 * these lists are sorted into for a reason.
 */
export function toggleValue(
  options: readonly MultiSelectOption[],
  value: readonly string[],
  v: string,
): string[] {
  const next = new Set(value);
  if (next.has(v)) next.delete(v);
  else next.add(v);
  return options.filter((o) => next.has(o.v)).map((o) => o.v);
}
